import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useAsync } from '../lib/useAsync.ts';
import { ErrorState, Loading, PageHeader } from '../components/ui.tsx';
import { formatDate, formatNumber } from '../lib/format.ts';
import type { CompetitorWithHistory } from '../../../shared/types.ts';
import { NewBadge } from '../components/NewBadge.tsx';
import { RoundTable } from '../components/RoundTable.tsx';
import { useCollections, type CollectionEntry } from '../lib/collections.ts';
import { useRounds, type Rounds } from '../lib/rounds.ts';
import type { RoundSection } from '../../../shared/types.ts';

interface LiveProfile {
  followers: number | null;
  posts: number | null;
  following: number | null;
  capturedAt: string;
}

interface Profile {
  handle: string;
  as_of: string;
  followers: number;
  following: number;
  posts: number;
  founded?: string;
  legacy?: {
    origin_year: number;
    origin_name: string;
    origin_note: string;
    founded_as_portal: number;
    founding_note: string;
    founders: string[];
    historical_milestones: { period: string; description: string }[];
    source_note?: string;
  };
  bio_current: string;
  bio_problems: string[];
  bio_proposals: { id: string; title: string; text: string; uso: string }[];
  highlights_current: string[];
  highlights_evaluation?: string;
  highlights_suggested_structure: string[];
  brand_identity_diagnosis: {
    evidence: string;
    interpretation: string;
    impact: string;
    recommendation: string;
    criteria_evaluation: { criterio: string; avaliacao: string; evidencia: string }[];
    manter?: string;
    padronizar?: string;
    eliminar_repensar?: string;
    introduzir?: string;
  };
  positioning?: Record<string, string>;
}

interface Findings {
  as_of: string;
  findings: {
    id: string;
    title: string;
    evidence: string;
    interpretation: string;
    recommendation: string;
    severity: 'critica' | 'alta' | 'media' | 'baixa';
  }[];
}

interface Partnerships {
  as_of: string;
  relationships: {
    partner: string;
    contact_name: string | null;
    contact_email: string | null;
    status: string;
    last_contact_date: string | null;
    last_contact_note: string | null;
    next_step: string | null;
    priority: string | null;
    risk_note: string | null;
  }[];
  media_kit_analysis: {
    file_name?: string;
    pages?: number;
    note?: string;
    structure?: string[];
    [key: string]: unknown;
  };
}

interface Meta {
  audit_dates: { primary: string; secondary_updates?: string[]; note?: string };
  sources: { name: string; type: string; collected: string; coverage: string }[];
  methodology_caveats: string[];
  not_measured: string[];
  data_export_howto?: { source: string; steps: string[] }[];
}

interface ContentPost {
  rank: number;
  theme: string;
  format: string;
  views_total: number;
  reach_accounts: number;
  non_followers_pct: number;
  interactions_total: number;
  likes: number;
  comments: number;
  saves: number;
  shares: number;
  engaged_accounts: number;
  profile_visits?: number;
  external_link_taps?: number;
  note?: string;
}

interface PeerTechnique {
  id: string;
  title: string;
  description: string;
  evidence_quote: string;
  results_cited?: string[];
  production_note?: string;
  applicability_to_evshadow: string;
}

interface PeerConversation {
  id: string;
  partner: string;
  handle: string;
  role: string;
  date: string;
  context: string;
  source_note: string;
  evfasbr_snapshot?: {
    as_of: string;
    followers: number;
    posts: number;
    following: number;
    views_last_30d: number;
    note: string;
  };
  techniques: PeerTechnique[];
  team_and_operations?: Record<string, string>;
  content_ideas_from_wendel?: string[];
  community_management_notes?: string[];
  bmg_relationship_note?: string;
}

interface ContentInsights {
  as_of: string;
  period_start: string;
  period_end: string;
  period_label: string;
  top_reels: ContentPost[];
  top_posts_carousels: ContentPost[];
  patterns: { id: string; title: string; evidence: string; recommendation: string }[];
  repost_test_suggestions: { id: string; action: string; why: string }[];
  peer_conversations?: PeerConversation[];
}

interface Strategy {
  as_of: string;
  source_note: string;
  brand_system: {
    palette_primary: { name: string; hex: string }[];
    palette_secondary: { name: string; hex: string }[];
    palette_rule: string;
    typography: string;
    frame_rules: string[];
    standardize: string[];
    eliminate: string[];
  };
  voice_system: {
    tension_note: string;
    identity: string;
    language_rule: string;
    caption_structure: string[];
    approved_hashtags: string[];
    emoji_rule: string;
    performance_notes: string[];
    never_do: string[];
  };
  seo: {
    observed: string;
    keywords_to_reinforce: string[];
    terms_to_avoid: string;
  };
  funnel: { stage: string; description: string }[];
  content_matrix: { objetivo: string; formato: string; tema: string; cta: string }[];
  editorial_plan_30d: { period: string; phase: string; focus: string }[];
  editorial_plan_90d: { period: string; phase: string; focus: string }[];
  do_not_change: string[];
  test_before_discard: string[];
  tools_evaluated: {
    saas: { name: string; plan: string; verdict: string }[];
    opensource: { name: string; url: string; category: string; note: string }[];
    notes: string[];
    adoption_order: string[];
  };
}

interface YoutubeChannel {
  as_of: string;
  collected: string;
  source_note: string;
  overview: {
    handle: string;
    channel_url: string;
    created: string;
    subscribers: number;
    videos_total: number;
    videos_long: number;
    videos_shorts: number;
    shorts_active_since: string;
    lifetime_views: number;
    lifetime_watch_hours: number;
    last_28d: {
      period_start: string;
      period_end: string;
      views: number;
      views_change_note: string;
      new_subscribers: number;
      subscribers_change_note: string;
    };
    note: string;
  };
  branding_findings: {
    id: string;
    severity: 'alta' | 'media' | 'baixa';
    title: string;
    evidence: string;
    impact: string;
  }[];
  catalog: {
    long_videos_note: string;
    top_video: { title: string; year: number; views: number; visibility: string };
    unlisted_high_quality: string[];
    copyright_notes: string[];
    shorts_note: string;
  };
  audience_lifetime: {
    period_start: string;
    period_end: string;
    new_viewers_pct: number;
    casual_viewers_pct: number;
    regular_viewers_pct: number;
    watch_time_not_subscribed_pct: number;
    watch_time_subscribed_pct: number;
    gender: { female_pct: number; male_pct: number };
    age_top_brackets: { range: string; pct: number }[];
    top_geographies: { country: string; pct: number }[];
    interpretation: string;
  };
  priorities: { priority: string; action: string; effort: string }[];
}

interface XTwitter {
  as_of: string;
  collected: string;
  source_note: string;
  overview: {
    handle: string;
    profile_url: string;
    created: string;
    followers: number;
    following: number;
    posts_total: number;
    media_total: number;
    verified: boolean;
    note: string;
  };
  branding_findings: {
    id: string;
    severity: 'alta' | 'media' | 'baixa';
    title: string;
    evidence: string;
    impact: string;
  }[];
  content: {
    cadence_note: string;
    recent_posts: {
      date: string;
      description: string;
      replies: number;
      reposts: number;
      likes: number;
      views: number | null;
    }[];
    top_performer_note: string;
  };
  priorities: { priority: string; action: string; effort: string }[];
}

const TABS = [
  'Perfil e marca',
  'Conteúdo',
  'Estratégia',
  'YouTube',
  'X (Twitter)',
  'Rodadas',
  'Achados',
  'Parcerias',
  'Método',
] as const;
type Tab = (typeof TABS)[number];

const SEVERITY_CLASS: Record<string, string> = {
  critica: 'badge-priority-urgent',
  alta: 'badge-priority-high',
  media: 'badge-priority-medium',
  baixa: 'badge-priority-low',
};

export function AuditPage() {
  const [tab, setTab] = useState<Tab>('Perfil e marca');

  const profile = useAsync(() => api.get<Profile>('/audit/profile'), []);
  const content = useAsync(() => api.get<ContentInsights>('/audit/content-insights'), []);
  const strategy = useAsync(() => api.get<Strategy>('/audit/strategy'), []);
  const youtube = useAsync(() => api.get<YoutubeChannel>('/audit/youtube-channel'), []);
  const xTwitter = useAsync(() => api.get<XTwitter>('/audit/x-twitter'), []);
  const findings = useAsync(() => api.get<Findings>('/audit/competitive-findings'), []);
  const partnerships = useAsync(() => api.get<Partnerships>('/audit/partnerships'), []);
  const meta = useAsync(() => api.get<Meta>('/audit/meta'), []);
  const competitors = useAsync(() => api.get<CompetitorWithHistory[]>('/competitors'), []);
  const collections = useCollections();
  const rounds = useRounds();
  const xEntry = collections.latestWith('x_twitter');
  // Seções da Auditoria que a coleta mais recente atualizou: ganham o selo "Novo" na aba.
  const tabSection: Partial<Record<Tab, RoundSection>> = {
    'Perfil e marca': 'perfil',
    YouTube: 'youtube',
    'X (Twitter)': 'x',
  };

  // Números do próprio perfil vêm da rodada de concorrência mais recente: o JSON da
  // auditoria é uma foto de 20/09/2026 e fica velho a cada nova coleta.
  const ownSnapshot = competitors.data
    ?.find((c) => c.handle === '@ev.shadow')
    ?.snapshots.at(-1);
  const liveProfile: LiveProfile | null = ownSnapshot
    ? {
        followers: ownSnapshot.followers,
        posts: ownSnapshot.postsCount,
        following:
          typeof ownSnapshot.metrics?.following === 'number' ? ownSnapshot.metrics.following : null,
        capturedAt: ownSnapshot.capturedAt,
      }
    : null;

  const loading = profile.loading || findings.loading || partnerships.loading || meta.loading;
  const firstError = profile.error ?? findings.error ?? partnerships.error ?? meta.error;

  if (loading) return <Loading label="Carregando a auditoria…" />;
  if (firstError) return <ErrorState message={firstError} onRetry={profile.reload} />;

  return (
    <>
      <PageHeader
        title="Auditoria estratégica"
        subtitle={
          meta.data
            ? (() => {
                const updates = meta.data.audit_dates.secondary_updates ?? [];
                const lastUpdate = updates.length > 0 ? updates[updates.length - 1] : null;
                const primary = `Rodada principal de ${formatDate(meta.data.audit_dates.primary)}`;
                const update = lastUpdate ? `, última atualização em ${formatDate(lastUpdate)}` : '';
                const imported = rounds.latest ? ` Última rodada importada: ${formatDate(rounds.latest.roundDate)}.` : '';
                return `${primary}${update}.${imported} Todo número carrega a data em que foi coletado.`;
              })()
            : undefined
        }
      />

      <div className="tabs" role="tablist" aria-label="Seções da auditoria">
        {TABS.map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={tab === name}
            className={`tab${tab === name ? ' is-active' : ''}`}
            onClick={() => setTab(name)}
          >
            {name}
            {tabSection[name] && rounds.latest && rounds.isNew(tabSection[name]!) && (
              <>
                {' '}
                <NewBadge date={rounds.latest.roundDate} />
              </>
            )}
          </button>
        ))}
      </div>

      {tab === 'Perfil e marca' && profile.data && (
        <ProfileTab profile={profile.data} live={liveProfile} />
      )}
      {tab === 'Conteúdo' && (
        <>
          {content.loading && <Loading label="Carregando conteúdo…" />}
          {content.error && <ErrorState message={content.error} onRetry={content.reload} />}
          {content.data && <ContentTab data={content.data} />}
        </>
      )}
      {tab === 'Estratégia' && (
        <>
          {strategy.loading && <Loading label="Carregando estratégia…" />}
          {strategy.error && <ErrorState message={strategy.error} onRetry={strategy.reload} />}
          {strategy.data && <StrategyTab data={strategy.data} />}
        </>
      )}
      {tab === 'YouTube' && (
        <>
          {youtube.loading && <Loading label="Carregando canal do YouTube…" />}
          {youtube.error && <ErrorState message={youtube.error} onRetry={youtube.reload} />}
          {youtube.data && (
            <YoutubeTab data={youtube.data} rounds={rounds} />
          )}
        </>
      )}
      {tab === 'X (Twitter)' && (
        <>
          {xTwitter.loading && <Loading label="Carregando X (Twitter)…" />}
          {xTwitter.error && <ErrorState message={xTwitter.error} onRetry={xTwitter.reload} />}
          {xTwitter.data && (
            <XTwitterTab data={xTwitter.data} collection={xEntry} rounds={rounds} />
          )}
        </>
      )}
      {tab === 'Rodadas' && <RoundsTab rounds={rounds} />}
      {tab === 'Achados' && findings.data && <FindingsTab findings={findings.data} />}
      {tab === 'Parcerias' && partnerships.data && <PartnershipsTab data={partnerships.data} />}
      {tab === 'Método' && meta.data && <MetaTab meta={meta.data} />}
    </>
  );
}

function ProfileTab({ profile, live }: { profile: Profile; live: LiveProfile | null }) {
  const brand = profile.brand_identity_diagnosis;
  const stat = (current: number | null | undefined, audited: number) => ({
    value: current ?? audited,
    note:
      current != null
        ? `em ${formatDate(live?.capturedAt)} · auditoria: ${formatNumber(audited)} em ${formatDate(profile.as_of)}`
        : `em ${formatDate(profile.as_of)}`,
  });
  const followers = stat(live?.followers, profile.followers);
  const posts = stat(live?.posts, profile.posts);
  const following = stat(live?.following, profile.following);
  return (
    <>
      <section className="grid-cards">
        <div className="card stat-tile">
          <span className="text-label text-muted">Seguidores</span>
          <strong className="text-hero tabular-nums">{formatNumber(followers.value)}</strong>
          <span className="text-micro text-muted">{followers.note}</span>
        </div>
        <div className="card stat-tile">
          <span className="text-label text-muted">Publicações</span>
          <strong className="text-hero tabular-nums">{formatNumber(posts.value)}</strong>
          <span className="text-micro text-muted">{posts.note}</span>
        </div>
        <div className="card stat-tile">
          <span className="text-label text-muted">Seguindo</span>
          <strong className="text-hero tabular-nums">{formatNumber(following.value)}</strong>
          <span className="text-micro text-muted">{following.note}</span>
        </div>
        {profile.founded && (
          <div className="card stat-tile">
            <span className="text-label text-muted">Desde</span>
            <strong className="text-hero">{profile.founded}</strong>
          </div>
        )}
      </section>

      <section className="card">
        <div className="card__header">
          <h2 className="text-title">Bio</h2>
        </div>
        <pre className="code-block">{profile.bio_current}</pre>
        <h3 className="text-label">Problemas identificados</h3>
        <ul className="bullet-list">
          {profile.bio_problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
        <h3 className="text-label">Propostas</h3>
        <div className="proposal-grid">
          {profile.bio_proposals.map((proposal) => (
            <article className="card card--raised" key={proposal.id}>
              <h4 className="text-body-strong">{proposal.title}</h4>
              <pre className="code-block">{proposal.text}</pre>
              <p className="text-micro text-muted">{proposal.uso}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="card">
        <div className="card__header">
          <h2 className="text-title">Identidade visual</h2>
        </div>
        <dl className="detail-grid">
          <div>
            <dt className="text-micro text-muted">Evidência</dt>
            <dd className="text-body">{brand.evidence}</dd>
          </div>
          <div>
            <dt className="text-micro text-muted">Interpretação</dt>
            <dd className="text-body">{brand.interpretation}</dd>
          </div>
          <div>
            <dt className="text-micro text-muted">Impacto</dt>
            <dd className="text-body">{brand.impact}</dd>
          </div>
          <div>
            <dt className="text-micro text-muted">Recomendação</dt>
            <dd className="text-body">{brand.recommendation}</dd>
          </div>
        </dl>

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Critério</th>
                <th>Avaliação</th>
                <th>Evidência</th>
              </tr>
            </thead>
            <tbody>
              {brand.criteria_evaluation.map((row) => (
                <tr key={row.criterio}>
                  <td>{row.criterio}</td>
                  <td>{row.avaliacao}</td>
                  <td className="text-muted">{row.evidencia}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card">
        <div className="card__header">
          <h2 className="text-title">Destaques</h2>
        </div>
        <p className="text-micro text-muted">Atuais: {profile.highlights_current.join(' · ')}</p>
        {profile.highlights_evaluation && (
          <p className="callout">{profile.highlights_evaluation}</p>
        )}
        <h3 className="text-label">Estrutura sugerida</h3>
        <ol className="numbered-list">
          {profile.highlights_suggested_structure.map((h) => (
            <li key={h}>{h}</li>
          ))}
        </ol>
      </section>

      {profile.legacy && (
        <section className="card">
          <div className="card__header">
            <h2 className="text-title">Legado e marcos históricos</h2>
          </div>
          <p className="text-body">{profile.legacy.founding_note}</p>
          <p className="text-micro text-muted">
            Origem em {profile.legacy.origin_year} como {profile.legacy.origin_name}. {profile.legacy.origin_note}
          </p>
          <p className="text-micro text-muted">
            Fundadores: {profile.legacy.founders.join(', ')}.
          </p>

          <ul className="timeline" style={{ marginTop: 'var(--space-4)' }}>
            {/* Ordem cronológica real, não alfabética — "auge do portal" vem
                antes de "2024" no tempo, mas perderia essa ordem num sort de
                string. Mantém a ordem em que veio na fonte (data/profile.json). */}
            {profile.legacy.historical_milestones.map((m) => (
              <li className="timeline__item timeline__item--legado" key={m.description}>
                <div className="timeline__date timeline__date--text">
                  <strong>{m.period}</strong>
                </div>
                <div className="timeline__body">
                  <p className="text-body">{m.description}</p>
                </div>
              </li>
            ))}
          </ul>

          {profile.legacy.source_note && (
            <p className="text-micro text-muted" style={{ marginTop: 'var(--space-3)' }}>
              {profile.legacy.source_note}
            </p>
          )}
        </section>
      )}
    </>
  );
}

function StrategyTab({ data }: { data: Strategy }) {
  const { brand_system, voice_system, seo, funnel, content_matrix, tools_evaluated } = data;

  return (
    <>
      <section className="card">
        <div className="card__header">
          <h2 className="text-title">Sistema de marca</h2>
        </div>
        <div className="stack">
          <div>
            <h3 className="text-label">Paleta</h3>
            <div className="swatch-row">
              {[...brand_system.palette_primary, ...brand_system.palette_secondary].map((c) => (
                <span key={c.hex} className="swatch" title={`${c.name} · ${c.hex}`}>
                  <span className="swatch__color" style={{ background: c.hex }} aria-hidden="true" />
                  <span className="text-micro text-muted">
                    {c.name} · {c.hex}
                  </span>
                </span>
              ))}
            </div>
            <p className="text-body text-muted">{brand_system.palette_rule}</p>
          </div>

          <div>
            <h3 className="text-label">Tipografia</h3>
            <p className="text-body text-muted">{brand_system.typography}</p>
          </div>

          <div className="split-2">
            <div>
              <h3 className="text-label">Padronizar</h3>
              <ul className="bullet-list">
                {brand_system.standardize.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-label">Eliminar</h3>
              <ul className="bullet-list">
                {brand_system.eliminate.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </div>
          </div>

          <div>
            <h3 className="text-label">Regras de moldura e capa</h3>
            <ol className="numbered-list">
              {brand_system.frame_rules.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="card">
        <div className="card__header">
          <h2 className="text-title">Tom de voz</h2>
        </div>
        <div className="stack">
          <p className="callout">{voice_system.tension_note}</p>
          <p className="text-body">{voice_system.identity}</p>
          <p className="text-body text-muted">{voice_system.language_rule}</p>

          <div>
            <h3 className="text-label">Estrutura da legenda</h3>
            <ol className="numbered-list">
              {voice_system.caption_structure.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
          </div>

          <div>
            <h3 className="text-label">Hashtags aprovadas</h3>
            <div className="chip-row">
              {voice_system.approved_hashtags.map((h) => (
                <span key={h} className="badge badge-status-neutral">
                  {h}
                </span>
              ))}
            </div>
          </div>

          <p className="text-micro text-muted">{voice_system.emoji_rule}</p>

          <div className="split-2">
            <div>
              <h3 className="text-label">O que performa bem</h3>
              <ul className="bullet-list">
                {voice_system.performance_notes.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-label">Nunca fazer</h3>
              <ul className="bullet-list">
                {voice_system.never_do.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="card">
        <div className="card__header">
          <h2 className="text-title">SEO / descoberta</h2>
        </div>
        <p className="text-body text-muted">{seo.observed}</p>
        <div className="chip-row">
          {seo.keywords_to_reinforce.map((k) => (
            <span key={k} className="badge badge-status-success">
              {k}
            </span>
          ))}
        </div>
        <p className="text-micro text-muted">{seo.terms_to_avoid}</p>
      </section>

      <section className="card">
        <div className="card__header">
          <h2 className="text-title">Funil de conteúdo</h2>
        </div>
        <ul className="timeline">
          {funnel.map((f) => (
            <li key={f.stage} className="timeline__item">
              <div className="timeline__date timeline__date--text">
                <strong>{f.stage}</strong>
              </div>
              <div className="timeline__body">
                <p className="text-body">{f.description}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="card table-wrap">
        <div className="card__header">
          <h2 className="text-title">Matriz de conteúdo (amostra)</h2>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Objetivo</th>
              <th>Formato</th>
              <th>Tema</th>
              <th>CTA</th>
            </tr>
          </thead>
          <tbody>
            {content_matrix.map((m) => (
              <tr key={`${m.objetivo}-${m.tema}`}>
                <td>{m.objetivo}</td>
                <td className="text-muted">{m.formato}</td>
                <td>{m.tema}</td>
                <td className="text-muted">{m.cta}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <EditorialPlanSection plan30d={data.editorial_plan_30d} plan90d={data.editorial_plan_90d} />

      <div className="split-2">
        <section className="card">
          <div className="card__header">
            <h2 className="text-title">Não deve ser alterado</h2>
          </div>
          <ul className="bullet-list">
            {data.do_not_change.map((d) => (
              <li key={d}>{d}</li>
            ))}
          </ul>
        </section>

        <section className="card">
          <div className="card__header">
            <h2 className="text-title">Testar antes de descartar</h2>
          </div>
          <ul className="bullet-list">
            {data.test_before_discard.map((d) => (
              <li key={d}>{d}</li>
            ))}
          </ul>
        </section>
      </div>

      <ToolsEvaluatedSection tools={tools_evaluated} />
    </>
  );
}

function EditorialPlanSection({
  plan30d,
  plan90d,
}: {
  plan30d: { period: string; phase: string; focus: string }[];
  plan90d: { period: string; phase: string; focus: string }[];
}) {
  return (
    <div className="split-2">
      <section className="card">
        <div className="card__header">
          <h2 className="text-title">Plano editorial — 30 dias</h2>
        </div>
        <ul className="timeline">
          {plan30d.map((p) => (
            <li key={p.phase} className="timeline__item">
              <div className="timeline__date timeline__date--text">
                <strong>{p.phase}</strong>
                <span className="text-micro text-muted">{p.period}</span>
              </div>
              <div className="timeline__body">
                <p className="text-body">{p.focus}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="card">
        <div className="card__header">
          <h2 className="text-title">Fases — 90 dias</h2>
        </div>
        <ul className="timeline">
          {plan90d.map((p) => (
            <li key={p.phase} className="timeline__item">
              <div className="timeline__date timeline__date--text">
                <strong>{p.phase}</strong>
                <span className="text-micro text-muted">{p.period}</span>
              </div>
              <div className="timeline__body">
                <p className="text-body">{p.focus}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function ToolsEvaluatedSection({
  tools,
}: {
  tools: Strategy['tools_evaluated'];
}) {
  return (
    <section className="card">
      <div className="card__header">
        <h2 className="text-title">Ferramentas avaliadas (sem custo)</h2>
      </div>
      <div className="stack">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Ferramenta</th>
                <th>Plano gratuito</th>
                <th>Veredito</th>
              </tr>
            </thead>
            <tbody>
              {tools.saas.map((t) => (
                <tr key={t.name}>
                  <td>{t.name}</td>
                  <td className="text-muted">{t.plan}</td>
                  <td>{t.verdict}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div>
          <h3 className="text-label">Open source (GitHub)</h3>
          <ul className="bullet-list">
            {tools.opensource.map((t) => (
              <li key={t.name}>
                <a className="link" href={t.url} target="_blank" rel="noreferrer noopener">
                  {t.name}
                </a>{' '}
                <span className="text-micro text-muted">({t.category})</span> — {t.note}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-label">Ordem de adoção sugerida</h3>
          <ol className="numbered-list">
            {tools.adoption_order.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ol>
        </div>

        {tools.notes.length > 0 && (
          <ul className="bullet-list">
            {tools.notes.map((n) => (
              <li key={n} className="text-micro text-muted">
                {n}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function YoutubeTab({ data, rounds }: { data: YoutubeChannel; rounds: Rounds }) {
  const { overview, branding_findings, catalog, audience_lifetime, priorities } = data;

  return (
    <>
      <RoundTable
        title="Canal EvShadow (@evshadowhd)"
        section="youtube"
        series={rounds.seriesOf('youtube.')}
        rounds={rounds.rounds}
        latest={rounds.latest}
        footer="Fonte: YouTube Studio do canal EvShadow (@evshadowhd). O restante desta aba continua sendo o retrato da auditoria de 19/09/2026."
      />
      <section className="card">
        <div className="card__header">
          <h2 className="text-title">Visão geral do canal</h2>
          <span className="text-micro text-muted">
            {overview.handle} · Coletado em {formatDate(data.collected)}
          </span>
        </div>
        <p className="text-body text-muted">{overview.note}</p>
        <div className="kv-grid">
          <div className="kv">
            <span className="text-micro text-muted">Inscritos</span>
            <strong className="text-title tabular-nums">{formatNumber(overview.subscribers)}</strong>
          </div>
          <div className="kv">
            <span className="text-micro text-muted">Vídeos</span>
            <strong className="text-title tabular-nums">
              {overview.videos_total} ({overview.videos_long} longos + {overview.videos_shorts} shorts)
            </strong>
          </div>
          <div className="kv">
            <span className="text-micro text-muted">Visualizações (lifetime, desde {formatDate(overview.created)})</span>
            <strong className="text-title tabular-nums">{formatNumber(overview.lifetime_views)}</strong>
          </div>
          <div className="kv">
            <span className="text-micro text-muted">
              Últimos 28 dias ({formatDate(overview.last_28d.period_start)} a {formatDate(overview.last_28d.period_end)})
            </span>
            <strong className="text-title tabular-nums">
              {formatNumber(overview.last_28d.views)} views · +{overview.last_28d.new_subscribers} inscritos
            </strong>
          </div>
        </div>
        <p className="text-micro text-muted">
          {overview.last_28d.views_change_note}; {overview.last_28d.subscribers_change_note}.
        </p>
      </section>

      <div className="stack">
        {branding_findings.map((f) => (
          <section className="card" key={f.id}>
            <div className="card__header">
              <h2 className="text-title">{f.title}</h2>
              <span className={`badge ${SEVERITY_CLASS[f.severity] ?? 'badge-status-neutral'}`}>
                {f.severity}
              </span>
            </div>
            <dl className="detail-grid">
              <div>
                <dt className="text-micro text-muted">Evidência</dt>
                <dd className="text-body">{f.evidence}</dd>
              </div>
              <div>
                <dt className="text-micro text-muted">Impacto</dt>
                <dd className="text-body">{f.impact}</dd>
              </div>
            </dl>
          </section>
        ))}
      </div>

      <section className="card">
        <div className="card__header">
          <h2 className="text-title">Catálogo</h2>
        </div>
        <div className="stack">
          <p className="text-body text-muted">{catalog.long_videos_note}</p>
          <p className="text-body">
            Maior sucesso histórico: <strong>{catalog.top_video.title}</strong> ({catalog.top_video.year}) —{' '}
            {formatNumber(catalog.top_video.views)} visualizações, {catalog.top_video.visibility}.
          </p>
          <div>
            <h3 className="text-label">Reuploads em HQ que ficam invisíveis (Não Listados)</h3>
            <ul className="bullet-list">
              {catalog.unlisted_high_quality.map((v) => (
                <li key={v}>{v}</li>
              ))}
            </ul>
          </div>
          <ul className="bullet-list">
            {catalog.copyright_notes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
          <p className="text-body text-muted">{catalog.shorts_note}</p>
        </div>
      </section>

      <section className="card">
        <div className="card__header">
          <h2 className="text-title">Audiência</h2>
          <span className="text-micro text-muted">
            Lifetime · {formatDate(audience_lifetime.period_start)} a {formatDate(audience_lifetime.period_end)}
          </span>
        </div>
        <div className="kv-grid">
          <div className="kv">
            <span className="text-micro text-muted">Espectadores novos</span>
            <strong className="text-title tabular-nums">{audience_lifetime.new_viewers_pct}%</strong>
          </div>
          <div className="kv">
            <span className="text-micro text-muted">Espectadores regulares</span>
            <strong className="text-title tabular-nums">{audience_lifetime.regular_viewers_pct}%</strong>
          </div>
          <div className="kv">
            <span className="text-micro text-muted">Tempo de exibição — não inscritos</span>
            <strong className="text-title tabular-nums">{audience_lifetime.watch_time_not_subscribed_pct}%</strong>
          </div>
          <div className="kv">
            <span className="text-micro text-muted">Gênero</span>
            <strong className="text-title tabular-nums">
              {audience_lifetime.gender.male_pct}% M · {audience_lifetime.gender.female_pct}% F
            </strong>
          </div>
        </div>

        <div className="split-2">
          <div>
            <h3 className="text-label">Faixas etárias predominantes</h3>
            <ul className="bullet-list">
              {audience_lifetime.age_top_brackets.map((a) => (
                <li key={a.range}>
                  {a.range} anos — {a.pct}%
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-label">Principais países</h3>
            <ul className="bullet-list">
              {audience_lifetime.top_geographies.map((g) => (
                <li key={g.country}>
                  {g.country} — {g.pct}%
                </li>
              ))}
            </ul>
          </div>
        </div>

        <p className="callout">{audience_lifetime.interpretation}</p>
      </section>

      <section className="card table-wrap">
        <div className="card__header">
          <h2 className="text-title">Prioridades</h2>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Prioridade</th>
              <th>Ação</th>
              <th>Esforço</th>
            </tr>
          </thead>
          <tbody>
            {priorities.map((p) => (
              <tr key={p.action}>
                <td className="text-muted">{p.priority}</td>
                <td>{p.action}</td>
                <td className="text-muted">{p.effort}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  );
}

function XTwitterTab({
  data,
  collection,
  rounds,
}: {
  data: XTwitter;
  collection: CollectionEntry | null;
  rounds: Rounds;
}) {
  const { overview, branding_findings, content, priorities } = data;
  const recent: { datetime: string; what: string; likes?: number; views?: number }[] =
    collection?.data.x_twitter?.recent_posts_utc ?? [];

  return (
    <>
      <RoundTable
        title="@evshadow"
        section="x"
        series={rounds.seriesOf('x.')}
        rounds={rounds.rounds}
        latest={rounds.latest}
        footer={
            recent.length > 0 ? (
              <>
                Posts mais recentes na coleta:{' '}
                {recent
                  .map((p) => `${formatDate(p.datetime)} "${p.what}" (${p.views ?? '—'} visualizações, ${p.likes ?? 0} curtidas)`)
                  .join('; ')}
                .
              </>
            ) : undefined
          }
      />
      <section className="card">
        <div className="card__header">
          <h2 className="text-title">Visão geral do perfil</h2>
          <span className="text-micro text-muted">
            {overview.handle} · Coletado em {formatDate(data.collected)}
          </span>
        </div>
        <p className="text-body text-muted">{overview.note}</p>
        <div className="kv-grid">
          <div className="kv">
            <span className="text-micro text-muted">Seguidores</span>
            <strong className="text-title tabular-nums">{formatNumber(overview.followers)}</strong>
          </div>
          <div className="kv">
            <span className="text-micro text-muted">Seguindo</span>
            <strong className="text-title tabular-nums">{formatNumber(overview.following)}</strong>
          </div>
          <div className="kv">
            <span className="text-micro text-muted">Posts (desde {formatDate(overview.created)})</span>
            <strong className="text-title tabular-nums">{formatNumber(overview.posts_total)}</strong>
          </div>
          <div className="kv">
            <span className="text-micro text-muted">Fotos e vídeos</span>
            <strong className="text-title tabular-nums">{formatNumber(overview.media_total)}</strong>
          </div>
        </div>
        <p className="text-micro text-muted">
          {overview.verified ? 'Conta verificada.' : 'Conta não verificada.'}
        </p>
      </section>

      <div className="stack">
        {branding_findings.map((f) => (
          <section className="card" key={f.id}>
            <div className="card__header">
              <h2 className="text-title">{f.title}</h2>
              <span className={`badge ${SEVERITY_CLASS[f.severity] ?? 'badge-status-neutral'}`}>
                {f.severity}
              </span>
            </div>
            <dl className="detail-grid">
              <div>
                <dt className="text-micro text-muted">Evidência</dt>
                <dd className="text-body">{f.evidence}</dd>
              </div>
              <div>
                <dt className="text-micro text-muted">Impacto</dt>
                <dd className="text-body">{f.impact}</dd>
              </div>
            </dl>
          </section>
        ))}
      </div>

      <section className="card table-wrap">
        <div className="card__header">
          <h2 className="text-title">Posts recentes</h2>
        </div>
        <p className="text-body text-muted">{content.cadence_note}</p>
        <table className="table">
          <thead>
            <tr>
              <th>Data</th>
              <th>Post</th>
              <th className="align-right">Respostas</th>
              <th className="align-right">Reposts</th>
              <th className="align-right">Curtidas</th>
              <th className="align-right">Views</th>
            </tr>
          </thead>
          <tbody>
            {content.recent_posts.map((p) => (
              <tr key={`${p.date}-${p.description}`}>
                <td className="text-muted tabular-nums">{formatDate(p.date)}</td>
                <td>{p.description}</td>
                <td className="align-right tabular-nums">{p.replies}</td>
                <td className="align-right tabular-nums">{p.reposts}</td>
                <td className="align-right tabular-nums">{formatNumber(p.likes)}</td>
                <td className="align-right tabular-nums">
                  {p.views === null ? '—' : formatNumber(p.views)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="callout">{content.top_performer_note}</p>
      </section>

      <section className="card table-wrap">
        <div className="card__header">
          <h2 className="text-title">Prioridades</h2>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Prioridade</th>
              <th>Ação</th>
              <th>Esforço</th>
            </tr>
          </thead>
          <tbody>
            {priorities.map((p) => (
              <tr key={p.action}>
                <td className="text-muted">{p.priority}</td>
                <td>{p.action}</td>
                <td className="text-muted">{p.effort}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  );
}

function FindingsTab({ findings }: { findings: Findings }) {
  const order = ['critica', 'alta', 'media', 'baixa'];
  const sorted = [...findings.findings].sort(
    (a, b) => order.indexOf(a.severity) - order.indexOf(b.severity),
  );

  return (
    <div className="stack">
      {sorted.map((finding) => (
        <section className="card" key={finding.id}>
          <div className="card__header">
            <h2 className="text-title">{finding.title}</h2>
            <span className={`badge ${SEVERITY_CLASS[finding.severity] ?? 'badge-status-neutral'}`}>
              {finding.severity}
            </span>
          </div>
          <dl className="detail-grid">
            <div>
              <dt className="text-micro text-muted">Evidência</dt>
              <dd className="text-body">{finding.evidence}</dd>
            </div>
            <div>
              <dt className="text-micro text-muted">Interpretação</dt>
              <dd className="text-body">{finding.interpretation}</dd>
            </div>
            <div>
              <dt className="text-micro text-muted">Recomendação</dt>
              <dd className="text-body">{finding.recommendation}</dd>
            </div>
          </dl>
        </section>
      ))}
    </div>
  );
}

function ContentPostCard({ post }: { post: ContentPost }) {
  return (
    <article className="card card--raised content-post-card">
      <div className="content-post-card__head">
        <span className="badge badge-status-neutral">#{post.rank}</span>
        <span className="text-body-strong">{post.theme}</span>
      </div>
      <span className="text-micro text-muted">{post.format}</span>

      <dl className="kv-grid" style={{ marginTop: 'var(--space-3)' }}>
        <div className="kv">
          <span className="text-micro text-muted">Visualizações</span>
          <strong className="tabular-nums">{formatNumber(post.views_total)}</strong>
        </div>
        <div className="kv">
          <span className="text-micro text-muted">Não seguidores</span>
          <strong className="tabular-nums">{post.non_followers_pct.toFixed(1).replace('.', ',')}%</strong>
        </div>
        <div className="kv">
          <span className="text-micro text-muted">Interações</span>
          <strong className="tabular-nums">{formatNumber(post.interactions_total)}</strong>
        </div>
        <div className="kv">
          <span className="text-micro text-muted">Compartilhamentos</span>
          <strong className="tabular-nums">{formatNumber(post.shares)}</strong>
        </div>
        {post.profile_visits !== undefined && (
          <div className="kv">
            <span className="text-micro text-muted">Visitas ao perfil</span>
            <strong className="tabular-nums">{formatNumber(post.profile_visits)}</strong>
          </div>
        )}
      </dl>

      {post.note && <p className="callout" style={{ marginTop: 'var(--space-3)' }}>{post.note}</p>}
    </article>
  );
}

function ContentTab({ data }: { data: ContentInsights }) {
  return (
    <>
      <p className="text-body">
        Janela de {data.period_label} — {formatDate(data.period_start)} a {formatDate(data.period_end)},
        coletado ao vivo em {formatDate(data.as_of)}.
      </p>

      <section className="card">
        <div className="card__header">
          <h2 className="text-title">Reels que mais performaram</h2>
        </div>
        <div className="proposal-grid">
          {data.top_reels.map((post) => (
            <ContentPostCard post={post} key={post.rank} />
          ))}
        </div>
      </section>

      <section className="card">
        <div className="card__header">
          <h2 className="text-title">Posts e carrosséis que mais performaram</h2>
        </div>
        <div className="proposal-grid">
          {data.top_posts_carousels.map((post) => (
            <ContentPostCard post={post} key={post.rank} />
          ))}
        </div>
      </section>

      <section className="card">
        <div className="card__header">
          <h2 className="text-title">Padrões identificados</h2>
        </div>
        <div className="stack">
          {data.patterns.map((p) => (
            <div key={p.id} className="content-pattern">
              <p className="text-body-strong">{p.title}</p>
              <p className="text-micro text-muted">{p.evidence}</p>
              <p className="callout">{p.recommendation}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="card callout-card">
        <div className="card__header">
          <h2 className="text-title">Ideias para repostar ou testar</h2>
        </div>
        <ul className="bullet-list">
          {data.repost_test_suggestions.map((s) => (
            <li key={s.id}>
              <strong>{s.action}</strong> — {s.why}
            </li>
          ))}
        </ul>
      </section>

      {data.peer_conversations?.map((conv) => (
        <PeerConversationSection conv={conv} key={conv.id} />
      ))}
    </>
  );
}

function PeerConversationSection({ conv }: { conv: PeerConversation }) {
  return (
    <section className="card">
      <div className="card__header">
        <h2 className="text-title">
          Aprendizados de conversa com {conv.partner} ({conv.handle})
        </h2>
        <span className="text-micro text-muted">{formatDate(conv.date)}</span>
      </div>

      <p className="text-body">{conv.context}</p>
      <p className="callout">{conv.source_note}</p>

      {conv.evfasbr_snapshot && (
        <div className="kv-grid" style={{ marginTop: 'var(--space-4)' }}>
          <div className="kv">
            <span className="text-micro text-muted">Seguidores ({conv.handle})</span>
            <strong className="tabular-nums">{formatNumber(conv.evfasbr_snapshot.followers)}</strong>
          </div>
          <div className="kv">
            <span className="text-micro text-muted">Visualizações (30d)</span>
            <strong className="tabular-nums">
              {formatNumber(conv.evfasbr_snapshot.views_last_30d)}
            </strong>
          </div>
          <div className="kv">
            <span className="text-micro text-muted">Publicações</span>
            <strong className="tabular-nums">{formatNumber(conv.evfasbr_snapshot.posts)}</strong>
          </div>
        </div>
      )}
      {conv.evfasbr_snapshot && (
        <p className="text-micro text-muted" style={{ marginTop: 'var(--space-2)' }}>
          {conv.evfasbr_snapshot.note}
        </p>
      )}

      <h3 className="text-label" style={{ marginTop: 'var(--space-6)' }}>
        Técnicas descritas
      </h3>
      <div className="stack">
        {conv.techniques.map((t) => (
          <article className="card card--raised" key={t.id}>
            <h4 className="text-body-strong">{t.title}</h4>
            <p className="text-body">{t.description}</p>
            <p className="text-micro text-muted content-quote">{t.evidence_quote}</p>
            {t.results_cited && t.results_cited.length > 0 && (
              <ul className="bullet-list">
                {t.results_cited.map((r) => (
                  <li key={r} className="text-micro text-muted">
                    {r}
                  </li>
                ))}
              </ul>
            )}
            {t.production_note && <p className="text-micro text-muted">{t.production_note}</p>}
            <p className="callout" style={{ marginTop: 'var(--space-3)' }}>
              {t.applicability_to_evshadow}
            </p>
          </article>
        ))}
      </div>

      {conv.team_and_operations && (
        <>
          <h3 className="text-label" style={{ marginTop: 'var(--space-6)' }}>
            Time e operação
          </h3>
          <dl className="detail-grid">
            {Object.values(conv.team_and_operations).map((v, i) => (
              <div key={i}>
                <dd className="text-body">{v}</dd>
              </div>
            ))}
          </dl>
        </>
      )}

      {conv.content_ideas_from_wendel && conv.content_ideas_from_wendel.length > 0 && (
        <>
          <h3 className="text-label" style={{ marginTop: 'var(--space-6)' }}>
            Ideias de conteúdo trocadas
          </h3>
          <ul className="bullet-list">
            {conv.content_ideas_from_wendel.map((idea) => (
              <li key={idea}>{idea}</li>
            ))}
          </ul>
        </>
      )}

      {conv.community_management_notes && conv.community_management_notes.length > 0 && (
        <>
          <h3 className="text-label" style={{ marginTop: 'var(--space-6)' }}>
            Gestão de comunidade
          </h3>
          <ul className="bullet-list">
            {conv.community_management_notes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </>
      )}

      {conv.bmg_relationship_note && (
        <>
          <h3 className="text-label" style={{ marginTop: 'var(--space-6)' }}>
            Relação com a BMG
          </h3>
          <p className="text-body">{conv.bmg_relationship_note}</p>
        </>
      )}
    </section>
  );
}

function PartnershipsTab({ data }: { data: Partnerships }) {
  return (
    <>
      <section className="card table-wrap">
        <div className="card__header">
          <h2 className="text-title">Relacionamentos</h2>
          <span className="text-micro text-muted">Situação em {formatDate(data.as_of)}</span>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Parceiro</th>
              <th>Contato</th>
              <th>Situação</th>
              <th>Último contato</th>
              <th>Próximo passo</th>
            </tr>
          </thead>
          <tbody>
            {data.relationships.map((rel) => (
              <tr key={rel.partner}>
                <td>
                  <strong>{rel.partner}</strong>
                  {rel.priority && (
                    <span className="text-micro text-muted"> · prioridade {rel.priority}</span>
                  )}
                </td>
                <td className="text-muted">
                  {rel.contact_name ?? '—'}
                  {rel.contact_email && (
                    <span className="text-micro"> ({rel.contact_email})</span>
                  )}
                </td>
                <td>{rel.status.replace(/_/g, ' ')}</td>
                <td className="text-muted tabular-nums">{formatDate(rel.last_contact_date)}</td>
                <td className="text-muted">{rel.next_step ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {data.relationships.some((r) => r.risk_note) && (
        <section className="card callout-card">
          <h2 className="text-title">Riscos apontados</h2>
          <ul className="bullet-list">
            {data.relationships
              .filter((r) => r.risk_note)
              .map((r) => (
                <li key={r.partner}>
                  <strong>{r.partner}:</strong> {r.risk_note}
                </li>
              ))}
          </ul>
        </section>
      )}

      <section className="card">
        <div className="card__header">
          <h2 className="text-title">Mídia kit</h2>
          {data.media_kit_analysis.file_name && (
            <span className="text-micro text-muted">
              {data.media_kit_analysis.file_name}
              {data.media_kit_analysis.pages ? ` · ${data.media_kit_analysis.pages} páginas` : ''}
            </span>
          )}
        </div>
        {data.media_kit_analysis.note && <p className="callout">{data.media_kit_analysis.note}</p>}
        {Object.entries(data.media_kit_analysis)
          .filter(([, value]) => Array.isArray(value) && value.every((v) => typeof v === 'string'))
          .map(([key, value]) => (
            <div key={key}>
              <h3 className="text-label">{key.replace(/_/g, ' ')}</h3>
              <ul className="bullet-list">
                {(value as string[]).map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
      </section>
    </>
  );
}

function MetaTab({ meta }: { meta: Meta }) {
  return (
    <>
      <section className="card table-wrap">
        <div className="card__header">
          <h2 className="text-title">Fontes</h2>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Fonte</th>
              <th>Tipo</th>
              <th>Coletada em</th>
              <th>Cobertura</th>
            </tr>
          </thead>
          <tbody>
            {meta.sources.map((source) => (
              <tr key={source.name}>
                <td>{source.name}</td>
                <td className="text-muted">{source.type}</td>
                <td className="text-muted tabular-nums">{source.collected}</td>
                <td className="text-muted">{source.coverage}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <div className="split-2">
        <section className="card">
          <div className="card__header">
            <h2 className="text-title">Ressalvas metodológicas</h2>
          </div>
          <ul className="bullet-list">
            {meta.methodology_caveats.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </section>

        <section className="card">
          <div className="card__header">
            <h2 className="text-title">O que não foi medido</h2>
          </div>
          <ul className="bullet-list">
            {meta.not_measured.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </section>
      </div>

      {meta.data_export_howto && meta.data_export_howto.length > 0 && (
        <section className="card">
          <div className="card__header">
            <h2 className="text-title">Como exportar os dados que faltam</h2>
          </div>
          <div className="stack">
            {meta.data_export_howto.map((h) => (
              <div key={h.source}>
                <h3 className="text-label">{h.source}</h3>
                <ol className="numbered-list">
                  {h.steps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}

const SECTION_LABEL: Record<RoundSection, string> = {
  perfil: 'Perfil',
  metricas: 'Métricas',
  concorrencia: 'Concorrência',
  youtube: 'YouTube',
  x: 'X',
  linktree: 'Linktree',
};

const SECTION_ORDER: RoundSection[] = ['perfil', 'metricas', 'concorrencia', 'youtube', 'x', 'linktree'];

const METRIC_GROUP_LABEL: Record<string, string> = {
  'metricas.ig_reels.': 'retenção de reels',
  'metricas.ig_demo.': 'demografia',
};

/**
 * Uma linha por rodada, uma coluna por seção (sempre as mesmas seis): mostra de relance o que cada
 * rodada coletou e o que ficou de fora, e por quê. Abaixo, as séries que não têm tela própria.
 */
function RoundsTab({ rounds }: { rounds: Rounds }) {
  if (rounds.loading) return <Loading label="Carregando as rodadas…" />;
  if (rounds.error) return <ErrorState message={rounds.error} onRetry={rounds.reload} />;
  const list = [...rounds.rounds].reverse();
  const latest = rounds.latest;
  return (
    <>
      <section className="card table-wrap">
        <div className="card__header">
          <h2 className="text-title">Cobertura de cada rodada</h2>
          <span className="text-micro text-muted">{rounds.rounds.length} rodada(s) importada(s)</span>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Rodada</th>
              {SECTION_ORDER.map((section) => (
                <th key={section}>{SECTION_LABEL[section]}</th>
              ))}
              <th>Faltou dentro de seções cobertas</th>
            </tr>
          </thead>
          <tbody>
            {list.map((round) => (
              <tr key={round.roundDate}>
                <td className="tabular-nums">{formatDate(round.roundDate)}</td>
                {SECTION_ORDER.map((section) => {
                  const covered = round.covers.includes(section);
                  const reason = round.notCovered.find((n) => n.section === section)?.reason;
                  return (
                    <td
                      key={section}
                      className={`coverage-cell ${covered ? 'coverage-cell--yes' : 'coverage-cell--no'}`}
                      title={covered ? undefined : reason}
                    >
                      {covered ? 'coberta' : 'não coberta'}
                    </td>
                  );
                })}
                <td className="text-micro text-muted">
                  {round.notCollected.length > 0
                    ? round.notCollected.map((n) => METRIC_GROUP_LABEL[n.metricPrefix] ?? n.metricPrefix).join(', ')
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {latest && (latest.notCovered.length > 0 || latest.notCollected.length > 0) && (
          <div>
            <p className="text-label">Por que faltou na rodada de {formatDate(latest.roundDate)}:</p>
            <ul className="text-micro text-muted">
              {latest.notCovered.map((n) => (
                <li key={n.section}>
                  <strong>{SECTION_LABEL[n.section]}</strong>: {n.reason}
                </li>
              ))}
              {latest.notCollected.map((n) => (
                <li key={n.metricPrefix}>
                  <strong>{METRIC_GROUP_LABEL[n.metricPrefix] ?? n.metricPrefix}</strong>: {n.reason}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
      <RoundTable
        title="Perfil do Instagram (@ev.shadow)"
        section="perfil"
        series={rounds.seriesOf('perfil.')}
        rounds={rounds.rounds}
        latest={rounds.latest}
      />
      <RoundTable
        title="Linktree (linktr.ee/evshadow)"
        section="linktree"
        series={rounds.seriesOf('linktree.')}
        rounds={rounds.rounds}
        latest={rounds.latest}
      />
    </>
  );
}
